import React, {useEffect, useState} from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import ReactPaginate from 'react-paginate';
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import {TableContainer} from '@material-ui/core';
import {ANALYTICS_ITEMS_PAGE_SIZE} from 'helpers/constants/analytics';
import {
  hasAnalyticsDateRange,
  resolveAnalyticsRequestDates
} from 'helpers/services/api/analyticsHelpers';
import {useAnalyticsFilters} from 'components/AnalyticsFilters/AnalyticsFiltersContext';
import {
  DashboardTable,
  NoResultText,
  TableImage,
  TableInline,
  TablePagination,
  TablePaginationText,
  TableSort,
  TableText
} from '../../../routes/App/dashboard/styles';
import TablePhoto from '../../../assets/icon-dish.svg';
import IconSortActiveUp from '../../../assets/icon-sort-active-up.svg';
import IconSortActiveDown from '../../../assets/icon-sort-active-down.svg';
import Loading from '../../shared/Utils/Loading';
import {HeadCells} from '../../../helpers/constants/Table';
import {useDatePickerContext} from '../../Datepicker/DatePickerContext';
import {GET_SALES_PER_ITEM} from '../../../store/analyticsSalesPerItem/action';
import roundTheNumber from '../../../helpers/functionUtils/roundTheNumber';

const getSortIcon = (headCellId, orderBy, order) => {
  if (orderBy !== headCellId) {
    return null;
  }
  return order === 'desc' ? IconSortActiveDown : IconSortActiveUp;
};

const SUPPORTED_SORT_DIRECTIONS = {
  rank: 'asc',
  name: 'asc',
  totalOrders: 'desc',
  totalSales: 'desc'
};

const SalesByItems = ({
  disableFetch,
  itemsOverride,
  totalCountOverride,
  loadingOverride,
  currentPageOverride,
  orderOverride,
  orderByOverride,
  onPageChangeOverride,
  onSortChangeOverride
}) => {
  const dispatch = useDispatch();
  const {locationId} = useSelector((state) => state.user);
  const {salesPerItem, totalCount, salesPerItemLoading} = useSelector(
    (state) => state.analyticsSalesPerItemReducer
  );

  const date = useDatePickerContext();
  const filters = useAnalyticsFilters();
  const isControlled = disableFetch;

  const [internalOrder, setInternalOrder] = useState(
    SUPPORTED_SORT_DIRECTIONS.totalOrders
  );
  const [internalOrderBy, setInternalOrderBy] = useState('totalOrders');
  const [internalCurrentPage, setInternalCurrentPage] = useState(0);

  const resolvedOrder = isControlled ? orderOverride : internalOrder;
  const resolvedOrderBy = isControlled ? orderByOverride : internalOrderBy;
  const resolvedCurrentPage = isControlled
    ? currentPageOverride
    : internalCurrentPage;
  const resolvedItems = isControlled ? itemsOverride ?? [] : salesPerItem;
  const resolvedTotalCount = isControlled
    ? totalCountOverride ?? 0
    : totalCount;
  const resolvedLoading = isControlled
    ? loadingOverride ?? false
    : salesPerItemLoading;

  const pageCount =
    Math.ceil(resolvedTotalCount / ANALYTICS_ITEMS_PAGE_SIZE) || 0;

  useEffect(() => {
    if (!isControlled) {
      setInternalCurrentPage(0);
    }
  }, [date, locationId, filters, resolvedOrderBy, resolvedOrder, isControlled]);

  useEffect(() => {
    if (
      disableFetch ||
      !locationId ||
      !hasAnalyticsDateRange(date) ||
      !resolvedOrderBy
    ) {
      return;
    }
    dispatch(
      GET_SALES_PER_ITEM({
        ...resolveAnalyticsRequestDates(date),
        page: resolvedCurrentPage,
        pageSize: ANALYTICS_ITEMS_PAGE_SIZE,
        orderBy: resolvedOrderBy,
        order: resolvedOrder,
        channelTypes: filters.channelTypes,
        fulfillmentModes: filters.fulfillmentModes
      })
    );
  }, [
    date,
    locationId,
    dispatch,
    filters.channelTypes,
    filters.fulfillmentModes,
    resolvedCurrentPage,
    resolvedOrderBy,
    resolvedOrder,
    disableFetch
  ]);

  const handlePageClick = ({selected: selectedPage}) => {
    if (isControlled) {
      onPageChangeOverride(selectedPage);
      return;
    }
    setInternalCurrentPage(selectedPage);
  };

  const handleRequestSort = (property) => {
    const nextSort = {
      order: SUPPORTED_SORT_DIRECTIONS[property] || 'asc',
      orderBy: property
    };

    if (isControlled) {
      onSortChangeOverride(nextSort);
      return;
    }

    setInternalOrder(nextSort.order);
    setInternalOrderBy(nextSort.orderBy);
  };

  const createSortHandler = (property) => () => {
    handleRequestSort(property);
  };

  const showingCount = resolvedItems.length;

  return (
    <>
      {resolvedLoading && <Loading />}
      {!resolvedLoading && (
        <>
          <DashboardTable>
            <TableContainer>
              <Table aria-label='enhanced table'>
                <TableHead>
                  <TableRow>
                    {HeadCells.map((headCell) => (
                      <TableCell
                        key={headCell.id}
                        sortDirection={
                          resolvedOrderBy === headCell.id
                            ? resolvedOrder
                            : false
                        }
                        align={headCell.alignLeft ? 'left' : 'inherit'}
                        className={
                          headCell.id === 'name' ? 'TableCellName' : ''
                        }
                      >
                        <TableSort
                          icon={getSortIcon(
                            headCell.id,
                            resolvedOrderBy,
                            resolvedOrder
                          )}
                          active={resolvedOrderBy === headCell.id}
                          direction={
                            resolvedOrderBy === headCell.id
                              ? resolvedOrder
                              : 'desc'
                          }
                          onClick={createSortHandler(headCell.id)}
                        >
                          {headCell.label}
                        </TableSort>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resolvedItems.map((row, index) => {
                    const labelId = `enhanced-table-checkbox-${index}`;
                    return (
                      <TableRow key={`${row.name}-${row.rank}`} tabIndex={-1}>
                        <TableCell
                          component='td'
                          scope='row'
                          inputprops={{'aria-labelledby': labelId}}
                        >
                          <TableText>{row.rank}</TableText>
                        </TableCell>
                        <TableCell style={{width: 350}} align='left'>
                          <TableInline>
                            <TableImage src={row.img || TablePhoto} />
                            <TableText>{row.name}</TableText>
                          </TableInline>
                        </TableCell>
                        <TableCell align='left'>
                          <TableText>{row.totalOrders}</TableText>
                        </TableCell>
                        <TableCell align='left'>
                          <TableText>
                            {roundTheNumber(row.totalSales)}
                          </TableText>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination>
              <TablePaginationText>
                Showing {showingCount > 0 ? showingCount : 0} from{' '}
                {resolvedTotalCount}
              </TablePaginationText>
              <ReactPaginate
                previousLabel='previous'
                nextLabel='next'
                breakLabel='...'
                breakClassName='break-me'
                pageCount={pageCount}
                forcePage={resolvedCurrentPage}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={handlePageClick}
                containerClassName={cn('pagination', {
                  remove: resolvedTotalCount === 0
                })}
                subContainerClassName='pages pagination'
                activeClassName='active'
              />
            </TablePagination>
          </DashboardTable>
        </>
      )}
      {!resolvedLoading && resolvedItems.length === 0 && (
        <Box display='flex' alignItems='center' justifyContent='center' m={1}>
          <NoResultText>No results found for this date range!</NoResultText>
        </Box>
      )}
    </>
  );
};

SalesByItems.defaultProps = {
  disableFetch: false,
  itemsOverride: null,
  totalCountOverride: undefined,
  loadingOverride: undefined,
  currentPageOverride: 0,
  orderOverride: SUPPORTED_SORT_DIRECTIONS.totalOrders,
  orderByOverride: 'totalOrders',
  onPageChangeOverride: () => {},
  onSortChangeOverride: () => {}
};

SalesByItems.propTypes = {
  disableFetch: PropTypes.bool,
  itemsOverride: PropTypes.arrayOf(PropTypes.object),
  totalCountOverride: PropTypes.number,
  loadingOverride: PropTypes.bool,
  currentPageOverride: PropTypes.number,
  orderOverride: PropTypes.string,
  orderByOverride: PropTypes.string,
  onPageChangeOverride: PropTypes.func,
  onSortChangeOverride: PropTypes.func
};

export default SalesByItems;
